import React, { useState } from "react";
import { Spin, Button, Tooltip, Modal, Form, Select, Input, InputNumber, DatePicker, message, notification } from "antd";
import { DownOutlined, GiftOutlined, PlusOutlined, MinusCircleOutlined, ImportOutlined, ExportOutlined, RollbackOutlined, EditOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import "./styles.scss";
import { logo, whiteLogo } from "../../../assets";
import { useTheme } from "../../../context/ThemeContext";
import { AddOnSessionDetailRoute, UserDetailRoute, UserDetailMembershipRoute, InvoiceDetailRoute, AllLockersRoute } from "../../../routes/routepath";
import { useUpdateCouponServicesMutation, useUnfreezeAddonMutation, useChangeTrainerMutation, useExtendAddonExpiryMutation } from "../../../services/membership";
import { useUpdateComplementaryServiceMutation, useGetComplementaryServicesQuery } from "../../../services/complimentaryCoupon";
import { useGetOpenTrainersQuery } from "../../../services/trainer";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import usePermissions from "../../../hooks/usePermissions";
import FreezeAddonModal from "../../modals/FreezeAddonModal";
import RefundModal from "../../modals/RefundModal";

/* ─── Domain types ─────────────────────────────────────────────────── */

interface Branch { _id: string; name?: string; }
interface PlanRef  { _id: string; name?: string; numberOfDays?: number; numberOfCoupons?: number; branchIds?: Branch[]; freezableSlot?: number; freezableDays?: number; }
interface Invoice {
  _id: string;
  invoiceNumber?: string;
  paymentType?: string;
  planName?: string;
  totalAmount?: number;
  salesPerson?: { name?: string };
  upgradedFromPlan?: { name?: string };
  upgradedToPlan?: { name?: string };
}
interface CouponService { serviceType: string; totalSessions: number; numberOfSessions?: number; remainingSessions?: number; }
interface ComplementaryServiceSession { _id?: string; coachId?: string; startedAt?: string; endedAt?: string; }
interface ComplementaryService { serviceType: string; couponId?: string; remainingSessions?: number; totalSessions?: number; sessions?: ComplementaryServiceSession[]; createdBy?: { name?: string }; }

// Per-session architecture: a coupon's session count now lives in `sessions[]`.
// `totalSessions` is kept as a fallback for records the backend hasn't migrated.
const getComplementarySessionCount = (svc?: ComplementaryService): number =>
  svc?.sessions?.length ?? svc?.totalSessions ?? 0;
interface PurchasedPlan { name?: string; }
interface FreezeRecord {
  freezeStartDate?: string;
  freezeEndDate?: string;
  freezeDays?: number;
  previousExpiryDate?: string;
  newExpiryDate?: string;
  reason?: string | null;
}
interface ExtendRecord {
  previousExpiryDate?: string;
  newExpiryDate?: string;
  daysExtended?: number;
  extendedAt?: string;
  reason?: string | null;
}
interface TransferParty {
  userId?: string;
  name?: string;
  phoneNumber?: string;
  membershipId?: string;
  transferredAt?: string;
}
interface TransferInfo {
  transferredTo?: TransferParty | null;
  transferredFrom?: TransferParty | null;
}

interface Membership {
  _id: string;
  type?: 'addon' | 'membership' | string;
  addonType?: string;
  displayLabel?: string;
  status?: string;
  isFrozen?: boolean;
  isBalanceClear?: boolean;
  isUpgraded?: boolean;
  isRenew?: boolean;
  isAdvanceRenewal?: boolean;
  advanceRenewDaysLeft?: number;
  startDate?: string;
  expiryDate?: string;
  originalExpiryDate?: string;
  currentExpiryDate?: string;
  previousExpiryDate?: string;
  newExpiryDate?: string;
  effectiveExpiryDate?: string;
  totalExtensions?: number;
  upgradeLabel?: string;
  saleType?: string;
  mainUser?: boolean;
  daysExtended?: number;
  freezableSlot?: number;
  freezableDays?: number;
  usedFreezeSlots?: number;
  totalFreezeSlots?: number;
  usedFreezeDays?: number;
  totalFreezeDays?: number;
  totalFrozenDays?: number;
  freezeDays?: number;
  freezeHistory?: FreezeRecord[];
  extendHistory?: ExtendRecord[];
  totalSessions?: number;
  remainingSessions?: number;
  coachId?: { _id?: string } | string;
  assignedTrainer?: string;
  planId?: PlanRef;
  invoices?: Invoice[];
  totalInvoiced?: number;
  couponServices?: CouponService[];
  complementaryServices?: ComplementaryService[];
  purchasedPlans?: PurchasedPlan[];
  transferInfo?: TransferInfo;
  refundDate?: string;
  refundExpiryDate?: string;
  refundAmount?: number;
  creditNoteNumber?: string;
  branchId?: Branch;
}

interface MembershipCardProps {
  membershipData?: { data?: Membership[] };
  isLoading?: boolean;
  onAddSession?: (membership: Membership) => void;
  fallbackBranchName?: string;
  customerName?: string;
  customerMobile?: string;
  // Lets the parent page refetch its own membership/addon list after a refund
  // (the refund mutation lives in a separate RTK Query API slice).
  onRefundSuccess?: () => void;
}

/* ─── Constants ──────────────────────────────────────────────────────── */

const SERVICE_OPTIONS = [
  { label: 'Personal Training', value: 'personal_training' },
  { label: 'Pilates',           value: 'pilates' },
  { label: 'Therapy',           value: 'therapy' },
  { label: 'EMS',               value: 'ems' },
  { label: 'MMA',               value: 'mma' },
];

/** Status → colour, matching the user status tags (active=green, inactive=red, freezed=blue, …). */
const statusColor = (status?: string): string => {
  switch (status?.toLowerCase()) {
    case 'active':   return '#52c41a';
    case 'inactive': return '#ff4d4f';
    case 'freezed':
    case 'frozen':   return '#1890ff';
    case 'pending':  return '#faad14';
    case 'block':    return '#fa541c';
    case 'advance':  return '#722ed1';
    case 'refunded': return '#f5222d';
    default:         return 'rgb(141 139 139)';
  }
};

/**
 * The membership's effective expiry for refund purposes: if freeze/extend history has
 * pushed the expiry forward (the card's own "New Expiry Date" line), that extended date
 * is the primary/current expiry and the original expiry becomes secondary — so the
 * Refund modal's Expiry Date range should be bound by the NEW expiry, not the old one.
 */
const getEffectiveExpiry = (membership: Membership, isLocker: boolean): string | undefined => {
  const extendDays = (membership.extendHistory?.reduce((s, e) => s + (e.daysExtended || 0), 0) || membership.daysExtended) ?? 0;
  const newExpiry = membership.type === 'addon'
    ? (membership.effectiveExpiryDate || membership.newExpiryDate)
    : (membership.newExpiryDate || membership.currentExpiryDate);
  const shownExpiry = (membership.type === 'addon' || isLocker)
    ? membership.expiryDate
    : (membership.previousExpiryDate || membership.originalExpiryDate);
  const changed = !!newExpiry && !!shownExpiry &&
    new Date(newExpiry).toDateString() !== new Date(shownExpiry).toDateString();
  if (!isLocker && newExpiry && (extendDays > 0 || changed)) return newExpiry;
  return shownExpiry || membership.expiryDate;
};

/**
 * True when the same user already has a later package of the same type/addonType
 * (i.e. this one has already been renewed), so Renew should not be offered again.
 */
const hasLaterRenewal = (membership: Membership, all: Membership[]): boolean => {
  if (!membership.startDate) return false;
  const start = new Date(membership.startDate).getTime();
  const closed = ['refunded', 'cancelled', 'canceled', 'expired', 'transferred'];
  return all.some(o =>
    o._id !== membership._id &&
    o.type === membership.type &&
    (o.addonType || '') === (membership.addonType || '') &&
    !!o.startDate && new Date(o.startDate).getTime() > start &&
    !closed.includes((o.status || '').toLowerCase())
  );
};

/** Format an ISO date as D-M-YYYY (e.g. "16-8-2026") using the stored (UTC) date-part. */
const fmtFreezeDate = (d?: string) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  // IST-midnight stored dates: shift +5:30 before reading the UTC date-part.
  const ist = new Date(dt.getTime() + 330 * 60 * 1000);
  return `${ist.getUTCDate()}-${ist.getUTCMonth() + 1}-${ist.getUTCFullYear()}`;
};

/* ─── Component ──────────────────────────────────────────────────────── */

const MembershipCard = ({ membershipData, isLoading, onAddSession, fallbackBranchName, customerName, customerMobile, onRefundSuccess }: MembershipCardProps) => {
  const { hasPermission } = usePermissions();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { id: userId } = useParams<{ id: string }>();
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});
  const [bottomTab, setBottomTab] = useState<Record<string, 'freeze' | 'extend'>>({});
  const setTab = (id: string, tab: 'freeze' | 'extend') => setBottomTab((prev) => ({ ...prev, [id]: tab }));
  const [couponModalVisible, setCouponModalVisible]   = useState(false);
  const [couponMembership, setCouponMembership]       = useState<Membership | null>(null);
  const [couponForm] = Form.useForm();
  const [updateCouponServices, { isLoading: addingService }] = useUpdateCouponServicesMutation();
  const [updateComplementaryService, { isLoading: savingServiceEdit }] = useUpdateComplementaryServiceMutation();
  const [editingServiceType, setEditingServiceType] = useState<string | null>(null);
  const [editSessionsValue,  setEditSessionsValue]  = useState<number | null>(null);
  const { data: complementaryServicesData, refetch: refetchComplementaryServices } = useGetComplementaryServicesQuery(
    couponMembership?._id as string,
    { skip: !couponModalVisible || !couponMembership?._id }
  );
  const alreadyAddedServices: ComplementaryService[] =
    (complementaryServicesData as any)?.data ?? (complementaryServicesData as any) ?? [];
  const [unfreezeAddon, { isLoading: unfreezing }]  = useUnfreezeAddonMutation();
  const [freezeModalVisible, setFreezeModalVisible] = useState(false);
  const [freezeMembership,   setFreezeMembership]   = useState<Membership | null>(null);
  const [changeTrainer, { isLoading: changingTrainer }] = useChangeTrainerMutation();
  const [trainerModalVisible,  setTrainerModalVisible]  = useState(false);
  const [trainerConfirmVisible, setTrainerConfirmVisible] = useState(false);
  const [trainerMembership,    setTrainerMembership]    = useState<Membership | null>(null);
  const [selectedTrainerId,    setSelectedTrainerId]    = useState<string | null>(null);
  const [addDaysMembership,    setAddDaysMembership]    = useState<Membership | null>(null);
  const [extraDays,            setExtraDays]            = useState<string>('');
  // Addon-only: MembershipCard's "Add Days" modal is rendered solely for addon
  // cards, so it hits the addon extend endpoint. Membership's own Days form
  // (membership/DaysForm.tsx) is separate and unchanged.
  const [extendExpiry, { isLoading: extendingDays }]   = useExtendAddonExpiryMutation();
  const [refundMembership, setRefundMembership] = useState<Membership | null>(null);

  const handleRefundClick = (e: React.MouseEvent, membership: Membership) => {
    e.stopPropagation();
    setRefundMembership(membership);
  };

  const handleRefundModalClose = () => setRefundMembership(null);

  const handleAddDaysClick = (e: React.MouseEvent, membership: Membership) => {
    e.stopPropagation();
    setAddDaysMembership(membership);
    setExtraDays('');
  };

  const handleAddDaysClose = () => {
    setAddDaysMembership(null);
    setExtraDays('');
  };

  const handleAddDaysSubmit = async () => {
    if (!addDaysMembership || !extraDays || Number(extraDays) <= 0) return;
    try {
      await (extendExpiry as any)({ membershipId: addDaysMembership._id, days: Number(extraDays) }).unwrap();
      handleAddDaysClose();
    } catch (err) {
      console.error('Extend add-on expiry error:', err);
    }
  };

  const branchId = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId: string | undefined =
    typeof branchId === 'object' ? (branchId?._id || branchId?.id) : branchId;
  const { data: trainersData } = useGetOpenTrainersQuery({ branchId: selectedBranchId || undefined, limit: 100 });

  const toggleCard = (membershipId: string) => {
    setExpandedCards(prev => ({ ...prev, [membershipId]: !prev[membershipId] }));
  };

  const handleChangeTrainerClick = (e: React.MouseEvent, membership: Membership) => {
    e.stopPropagation();
    setTrainerMembership(membership);
    const coachId = typeof membership.coachId === 'object'
      ? membership.coachId?._id
      : membership.coachId;
    setSelectedTrainerId(coachId || membership.assignedTrainer || null);
    setTrainerModalVisible(true);
  };

  const handleTrainerModalClose = () => {
    setTrainerModalVisible(false);
    setTrainerMembership(null);
    setSelectedTrainerId(null);
  };

  const handleTrainerSubmit = () => {
    if (!selectedTrainerId || !trainerMembership) return;
    setTrainerConfirmVisible(true);
  };

  const handleTrainerConfirm = async () => {
    if (!selectedTrainerId || !trainerMembership) return;
    try {
      await (changeTrainer as any)({ membershipId: trainerMembership._id, coachId: selectedTrainerId }).unwrap();
      setTrainerConfirmVisible(false);
      handleTrainerModalClose();
    } catch (err) {
      console.error('Change trainer error:', err);
    }
  };

  const handleFreezeToggle = async (e: React.MouseEvent, membership: Membership) => {
    e.stopPropagation();
    if (membership.isFrozen) {
      try {
        await (unfreezeAddon as any)(membership._id).unwrap();
      } catch (err) {
        console.error('Unfreeze error:', err);
      }
      return;
    }
    setFreezeMembership(membership);
    setFreezeModalVisible(true);
  };

  const handleFreezeModalClose = () => {
    setFreezeModalVisible(false);
    setFreezeMembership(null);
  };

  const handleCouponTagClick = (e: React.MouseEvent, membership: Membership) => {
    e.stopPropagation();
    setCouponMembership(membership);
    couponForm.resetFields();
    setCouponModalVisible(true);
  };

  const handleCouponModalClose = () => {
    setCouponModalVisible(false);
    setCouponMembership(null);
    couponForm.resetFields();
    setEditingServiceType(null);
    setEditSessionsValue(null);
  };

  const handleEditServiceStart = (svc: ComplementaryService) => {
    setEditingServiceType(svc.serviceType);
    setEditSessionsValue(getComplementarySessionCount(svc));
  };

  const handleEditServiceCancel = () => {
    setEditingServiceType(null);
    setEditSessionsValue(null);
  };

  const handleEditServiceSave = async (svc: ComplementaryService) => {
    if (!couponMembership?._id || editSessionsValue == null || !svc.couponId) return;
    if (editSessionsValue < 1) {
      message.error('Sessions must be at least 1');
      return;
    }
    const { serviceType, couponId } = svc;
    try {
      await updateComplementaryService({
        membershipId: couponMembership._id,
        serviceType: couponId,
        body: { totalSessions: editSessionsValue },
      }).unwrap();
      setCouponMembership(prev => prev ? {
        ...prev,
        complementaryServices: prev.complementaryServices?.map(s =>
          s.serviceType === serviceType ? { ...s, totalSessions: editSessionsValue, sessions: undefined } : s
        ),
      } : prev);
      // message.success('Service updated successfully');
      handleEditServiceCancel();
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    if (!errorFields?.length) return;
    const labels = errorFields.map(f => {
      const name = Array.isArray(f.name) ? f.name[0] : f.name;
      return String(name).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    });
    notification.error({
      message: 'Required Fields Missing',
      description: [...new Set(labels)].join(', '),
      placement: 'topRight',
      duration: 4,
    });
  };

  const handleCouponSubmit = async (values: { services?: { serviceType: string; numberOfSessions: number }[] }) => {
    const services = values.services || [];
    if (services.length === 0) {
      message.warning('Please add at least one service');
      return;
    }
    try {
      await (updateCouponServices as any)({
        membershipId: couponMembership?._id,
        totalCoupons: couponMembership?.planId?.numberOfCoupons || 0,
        couponServices: services.map(svc => ({
          serviceType: svc.serviceType,
          numberOfSessions: svc.numberOfSessions,
        })),
      }).unwrap();
      refetchComplementaryServices();
      handleCouponModalClose();
    } catch (err) {
      console.error('Coupon submit error:', err);
    }
  };

  const handleCardClick = (membership: Membership) => {
    if (membership.type === 'addon') {
      if (!hasPermission('ADDON_SVC_VIEW_SESSION_DETAIL')) return;
      navigate(`${UserDetailRoute}/${userId}/addon-service${AddOnSessionDetailRoute}/${membership._id}`);
    } else if (membership.type === 'membership') {
      if (!hasPermission('VIEW_ADDON_SESSION_DETAIL')) return;
      navigate(`${UserDetailRoute}/${userId}/membership${AddOnSessionDetailRoute}/${membership._id}`);
    }
  };

  // Dates are stored as IST-midnight (…T18:30:00.000Z === 00:00 IST next day),
  // so shift by the IST offset (+5:30) before reading the UTC date-part. This
  // renders the calendar date the user actually picked, independent of the
  // viewer's local timezone (e.g. 2026-05-31T18:30:00Z → 01/06/2026).
  const formatDate = (date?: string) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'N/A';
    const ist = new Date(d.getTime() + 330 * 60 * 1000);
    const dd = String(ist.getUTCDate()).padStart(2, '0');
    const mm = String(ist.getUTCMonth() + 1).padStart(2, '0');
    return `${dd}-${mm}-${ist.getUTCFullYear()}`;
  };

  if (isLoading) {
    return (
      <div className="membership-card" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const memberships: Membership[] = membershipData?.data || [];

  if (memberships.length === 0) {
    return (
      <div className="membership-card">
        <div className="card-body">
          <p style={{ textAlign: 'center', padding: '20px' }}>No membership found</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="membership-cards-container">
        {memberships.map((membership) => {
          const status      = membership.status?.toLowerCase();
          const isActive    = status === 'active' || status === 'frozen' || status === 'freezed';
          const isExpanded  = expandedCards[membership._id];
          const statusClr   = statusColor(membership.status);
          const borderColor = `1px solid ${statusClr}`;
          const numberOfCoupons = membership.planId?.numberOfCoupons;
          const isLocker = membership.type === 'locker' || membership.addonType === 'locker';
          const canRenew = (!!membership.isRenew || !!membership.isAdvanceRenewal) && !hasLaterRenewal(membership, memberships);
          const lockerDays = (() => {
            if (!isLocker || !membership.startDate || !membership.expiryDate) return null;
            const s = new Date(membership.startDate).getTime();
            const e = new Date(membership.expiryDate).getTime();
            if (isNaN(s) || isNaN(e)) return null;
            return Math.max(0, Math.round((e - s) / 86400000));
          })();

          return (
            <div
              key={membership._id}
              className={`membership-card ${isExpanded ? 'expanded' : 'collapsed'} ${membership.type === 'addon' ? 'addon-card' : ''}`}
              style={{ border: borderColor, cursor: 'pointer', '--card-status': statusClr } as React.CSSProperties}
              onClick={() => handleCardClick(membership)}
            >
              <div className="ribbon">
                <div className="ribbon-content">
                  <span className="ribbon-star">★</span>
                  <span className="ribbon-status">{membership.status}</span>
                </div>
              </div>

              {membership.type === 'membership' && (numberOfCoupons ?? 0) > 0 && (
                <div className="coupon-tag" onClick={(e) => handleCouponTagClick(e, membership)}>
                  <GiftOutlined />
                  <span>{numberOfCoupons}</span>
                </div>
              )}

              <div className="card-header-collapsed">
                <div className="header-left">
                  <img src={theme === "light" ? logo : whiteLogo} alt="FitClub" className="logo" />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingLeft: 27 }}>
                    {(membership.saleType || membership.upgradeLabel) ? (
                      <span
                        style={{
                          display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                          fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                          background: 'rgba(82,196,26,0.15)', color: '#52c41a',
                        }}
                      >
                        {(membership.saleType || membership.upgradeLabel)!.replace(/_/g, ' ')}
                      </span>
                    ) : <span />}
                    {(() => {
                      // The membership's own branch (where this specific instance was bought)
                      // takes priority — the plan's branchIds list is only which branches
                      // the PLAN is sold at, not which one this membership belongs to.
                      if (membership.branchId?.name) {
                        return <p className="branch-name" style={{ width: 'auto' }}>{membership.branchId.name}</p>;
                      }
                      const branches = membership.planId?.branchIds || [];
                      if (branches.length === 0) return <p className="branch-name" style={{ width: 'auto' }}>{fallbackBranchName || 'N/A'}</p>;
                      const firstBranch    = branches[0]?.name || 'N/A';
                      const remainingCount = branches.length - 1;
                      const allBranchNames = branches.map(b => b?.name).filter(Boolean).join(', ');
                      if (remainingCount > 0) {
                        return <Tooltip title={allBranchNames}><p className="branch-name" style={{ width: 'auto' }}>{firstBranch} +{remainingCount}</p></Tooltip>;
                      }
                      return <p className="branch-name" style={{ width: 'auto' }}>{firstBranch}</p>;
                    })()}
                  </div>
                </div>
                <div className="header-right">
                  <div>
                    {(membership.purchasedPlans?.length ?? 0) >= 2 ? (
                      <h2 className="plan-title">
                        {membership.purchasedPlans![0]?.name}
                        <span style={{ margin: '0 8px', fontSize: '18px', fontWeight: '300' }}>→</span>
                        {membership.purchasedPlans![membership.purchasedPlans!.length - 1]?.name}
                      </h2>
                    ) : membership.invoices?.some(inv => inv.upgradedFromPlan && inv.upgradedToPlan) ? (
                      <h2 className="plan-title">
                        {membership.invoices.find(inv => inv.upgradedFromPlan && inv.upgradedToPlan)?.upgradedFromPlan?.name}
                        <span style={{ margin: '0 8px', fontSize: '18px', fontWeight: '300' }}>→</span>
                        {membership.invoices.find(inv => inv.upgradedFromPlan && inv.upgradedToPlan)?.upgradedToPlan?.name}
                      </h2>
                    ) : (
                      <h2
                        className="plan-title"
                        onClick={isLocker ? (e) => { e.stopPropagation(); navigate(AllLockersRoute); } : undefined}
                        style={isLocker ? { cursor: 'pointer', textDecoration: 'underline' } : undefined}
                      >
                        {membership.planId?.name || (isLocker ? membership.displayLabel : '') || 'N/A'}
                      </h2>
                    )}
                    <p className="plan-type">
                      {membership.type === 'addon' && membership.addonType
                        ? membership.addonType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                        : membership.type || 'membership'}
                    </p>
                  </div>
                  <button
                    className="view-btn"
                    onClick={(e) => { e.stopPropagation(); toggleCard(membership._id); }}
                  >
                    View <DownOutlined style={{ fontSize: '12px', marginLeft: '4px', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }} />
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div className="card-body">
                  <div className="info">
                    {(() => {
                      const party = membership.transferInfo?.transferredFrom || membership.transferInfo?.transferredTo;
                      if (!party) return null;
                      const isIncoming = !!membership.transferInfo?.transferredFrom;
                      return (
                        <div
                          className={`transfer-info-badge ${isIncoming ? 'transfer-in' : 'transfer-out'}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`${UserDetailRoute}/${party.userId}${UserDetailMembershipRoute}`); }}
                        >
                          <div className="transfer-icon">
                            {isIncoming ? <ImportOutlined /> : <ExportOutlined />}
                          </div>
                          <div className="transfer-details">
                            <span className="transfer-label">{isIncoming ? 'Transferred From' : 'Transferred To'}</span>
                            <span className="transfer-name">{party.name}</span>
                            <span className="transfer-meta">{party.phoneNumber} · {formatDate(party.transferredAt)}</span>
                          </div>
                        </div>
                      );
                    })()}

                    {membership.invoices && membership.invoices.length > 0 && (
                      <div className="invoices-section">
                        {membership.invoices.map((invoice) => (
                          <div key={invoice._id} style={{ marginBottom: '12px' }}>
                            <p style={{ padding: 0, margin: 0 }}>
                              <strong>Invoice ({invoice.paymentType?.replace(/_/g, ' ') || 'N/A'}):</strong>
                              {hasPermission('9-ud-addon-view-invoice') ? (
                                <span
                                  style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
                                  onClick={(e) => { e.stopPropagation(); navigate(`${InvoiceDetailRoute}/${invoice._id}`); }}
                                >
                                  {invoice.invoiceNumber}
                                </span>
                              ) : (
                                <span onClick={(e) => e.stopPropagation()}>{invoice.invoiceNumber}</span>
                              )}
                            </p>
                            {invoice.planName && (
                              <p style={{ fontSize: '12px', color: '#7e7e7eff', margin: '2px 0' }}>
                                <strong>Plan:</strong> <span>{invoice.planName}</span>
                              </p>
                            )}
                            {membership.type !== 'addon' && invoice.salesPerson && (
                              <p style={{ fontSize: '12px', color: '#7e7e7eff', margin: '2px 0' }}>
                                <strong>Sales Person:</strong> <span>{invoice.salesPerson.name || 'N/A'}</span>
                              </p>
                            )}
                            {membership.status === 'refunded' && (membership.refundDate || membership.refundExpiryDate || membership.refundAmount != null || membership.creditNoteNumber) && (
                              <div className="refund-info-box">
                                {membership.refundDate && (
                                  <div className="refund-info-row">
                                    <span className="refund-info-label">Refund Date</span>
                                    <span className="refund-info-value">{formatDate(membership.refundDate)}</span>
                                  </div>
                                )}
                                {membership.refundExpiryDate && (
                                  <div className="refund-info-row">
                                    <span className="refund-info-label">Member Expiry Date</span>
                                    <span className="refund-info-value">{formatDate(membership.refundExpiryDate)}</span>
                                  </div>
                                )}
                                {membership.refundAmount != null && (
                                  <div className="refund-info-row">
                                    <span className="refund-info-label">Refund Amount</span>
                                    <span className="refund-info-value">₹{membership.refundAmount.toLocaleString()}</span>
                                  </div>
                                )}
                                {membership.creditNoteNumber && (
                                  <div className="refund-info-row">
                                    <span className="refund-info-label">Credit Note</span>
                                    {hasPermission('9-ud-addon-view-invoice') && invoice._id ? (
                                      <span
                                        className="refund-info-value refund-info-link"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`${InvoiceDetailRoute}/${invoice._id}?from=credit-note`, {
                                            state: {
                                              creditNote: {
                                                creditNoteNumber:  membership.creditNoteNumber,
                                                refundAmount:      membership.refundAmount,
                                                paymentRefundDate: membership.refundDate,
                                                expiryDate:        membership.expiryDate,
                                              },
                                            },
                                          });
                                        }}
                                      >
                                        {membership.creditNoteNumber}
                                      </span>
                                    ) : (
                                      <span className="refund-info-value">{membership.creditNoteNumber}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <p><strong>Duration:</strong> <span>{(isLocker ? (lockerDays ?? 0) : (membership.planId?.numberOfDays || 0))} days</span></p>

                    {membership.type === 'addon' ? (
                      <>
                        <p>
                          <strong>Trainer:</strong>{' '}
                          <span>{(() => {
                            const coachId = typeof membership.coachId === 'object'
                              ? membership.coachId?._id
                              : membership.coachId;
                            const list: any[] = (trainersData as any)?.trainers || (trainersData as any)?.data || [];
                            const found = list.find((t: any) => t._id === coachId);
                            return found?.name || found?.user?.name || '-';
                          })()}</span>
                        </p>
                        <p><strong>Sessions:</strong> <span>{membership.totalSessions || 0}/{membership.remainingSessions || 0}</span></p>
                        {(() => {
                          const addDays = (membership.extendHistory?.reduce((s, e) => s + (e.daysExtended || 0), 0) || membership.daysExtended) ?? 0;
                          return (
                            <p><strong>Add Days:</strong> <span style={{ color: addDays > 0 ? '#52c41a' : undefined }}>{addDays} {addDays === 1 ? 'day' : 'days'}</span></p>
                          );
                        })()}
                        {(membership.complementaryServices?.length ?? 0) > 0 && membership.complementaryServices!.map((svc, i) => (
                          <p key={i}>
                            <strong>Complementary </strong>{' '}
                            <span style={{ color: (svc.remainingSessions ?? 0) > 0 ? '#52c41a' : '#999' }}>
                              {svc.remainingSessions ?? 0}/{getComplementarySessionCount(svc)}
                            </span>
                          </p>
                        ))}
                      </>
                    ) : (
                      <>
                        {!isLocker && (
                          <>
                            <p><strong>Freeze Slots:</strong> <span>{membership.usedFreezeSlots ?? 0}/{membership.planId?.freezableSlot ?? membership.totalFreezeSlots ?? 0}</span></p>
                            <p><strong>Freeze Days:</strong> <span>{membership.usedFreezeDays ?? 0}/{membership.planId?.freezableDays ?? membership.totalFreezeDays ?? 0}</span></p>
                          </>
                        )}
                        {(() => {
                          const extendDays = (membership.extendHistory?.reduce((s, e) => s + (e.daysExtended || 0), 0) || membership.daysExtended) ?? 0;
                          return extendDays > 0 ? (
                            <p><strong>Extended Days:</strong> <span style={{ color: '#52c41a' }}>{extendDays} {extendDays === 1 ? 'day' : 'days'}</span></p>
                          ) : null;
                        })()}
                      </>
                    )}

                    <p><strong>Start:</strong>  <span>{formatDate(membership.startDate)}</span></p>
                    {(() => {
                      const shownExpiry = (membership.type === 'addon' || isLocker)
                        ? membership.expiryDate
                        : (membership.previousExpiryDate || membership.originalExpiryDate);
                      return <p><strong>Expiry:</strong> <span>{formatDate(shownExpiry)}</span></p>;
                    })()}
                    {!isLocker && (() => {
                      const extendDays = (membership.extendHistory?.reduce((s, e) => s + (e.daysExtended || 0), 0) || membership.daysExtended) ?? 0;
                      // New expiry after any extension OR freeze adjustment.
                      const newExpiry = membership.type === 'addon'
                        ? (membership.effectiveExpiryDate || membership.newExpiryDate)
                        : (membership.newExpiryDate || membership.currentExpiryDate);
                      const shownExpiry = membership.type === 'addon'
                        ? membership.expiryDate
                        : (membership.previousExpiryDate || membership.originalExpiryDate);
                      const changed = newExpiry && shownExpiry &&
                        new Date(newExpiry).toDateString() !== new Date(shownExpiry).toDateString();
                      return newExpiry && (extendDays > 0 || changed) ? (
                        <p><strong>New Expiry Date:</strong> <span style={{ color: '#52c41a' }}>{formatDate(newExpiry)}</span></p>
                      ) : null;
                    })()}
                    <p><strong>Status:</strong> <span style={{ textTransform: 'capitalize', color: statusClr }}>{membership.status}</span></p>

                    {membership.type === 'addon' && isActive && (
                      <>
                        <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                          {onAddSession && hasPermission('9-ud-addon-add-session') && (
                            <Button
                              onClick={(e) => { e.stopPropagation(); onAddSession(membership); }}
                              style={{ flex: 1, background: 'var(--hover-bg)', borderColor: 'var(--muted)', color: 'var(--sider-text)' }}
                            >
                              Add Session
                            </Button>
                          )}
                          {hasPermission('9-ud-addon-freeze') && (
                            <Button
                              type={membership.isFrozen ? 'default' : 'primary'}
                              danger={membership.isFrozen}
                              loading={unfreezing}
                              onClick={(e) => handleFreezeToggle(e, membership)}
                              style={membership.isFrozen ? { flex: 1, background: 'var(--hover-bg)', borderColor: '#ff4d4f', color: '#ff4d4f' } : { flex: 1 }}
                            >
                              {membership.isFrozen ? 'Unfreeze Add-On' : 'Freeze Add-On'}
                            </Button>
                          )}
                        </div>
                        <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                          {hasPermission('9-ud-addon-change-trainer') && (
                            <Button
                              onClick={(e) => handleChangeTrainerClick(e, membership)}
                              style={{ flex: 1, background: 'var(--hover-bg)', borderColor: 'var(--muted)', color: 'var(--sider-text)' }}
                            >
                              Change Trainer
                            </Button>
                          )}
                          {hasPermission('9-ud-extend-days') && (
                            <Button
                              onClick={(e) => handleAddDaysClick(e, membership)}
                              style={{ flex: 1, background: 'var(--hover-bg)', borderColor: 'var(--muted)', color: 'var(--sider-text)' }}
                            >
                              Add Days
                            </Button>
                          )}
                        </div>
                        {(membership.isBalanceClear || membership.isUpgraded || canRenew) && (
                          <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                            {membership.isBalanceClear && hasPermission('9-ud-addon-clear-balance') && (
                              <Button
                                danger
                                onClick={(e) => { e.stopPropagation(); navigate(`${UserDetailRoute}/${userId}/addon-clear-balance/${membership._id}`); }}
                                style={{ flex: 1, background: 'var(--hover-bg)', borderColor: '#ff4d4f', color: '#ff4d4f' }}
                              >
                                Clear Balance
                              </Button>
                            )}
                            {membership.isUpgraded && hasPermission('9-ud-addon-upgrade') && (
                              <Button
                                onClick={(e) => { e.stopPropagation(); navigate(`${UserDetailRoute}/${userId}/addon-upgrade/${membership._id}`); }}
                                style={{ flex: 1, background: 'var(--hover-bg)', borderColor: 'var(--muted)', color: 'var(--sider-text)' }}
                              >
                                Upgrade
                              </Button>
                            )}
                            {canRenew && hasPermission('9-ud-addon-renew') && (
                              <Button
                                onClick={(e) => { e.stopPropagation(); navigate(`${UserDetailRoute}/${userId}/addon-renew/${membership._id}`); }}
                                style={{ flex: 1, background: 'var(--hover-bg)', borderColor: 'var(--muted)', color: 'var(--sider-text)' }}
                              >
                                Advance Renew
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {membership.status === 'active' && (membership.type !== 'addon' || membership.mainUser === true) && hasPermission(membership.type === 'addon' ? 'ADDON_MEMBERSHIP_REFUND' : 'CREDIT_NOTE_CREATE') && (
                      <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                        <Button
                          danger
                          icon={<RollbackOutlined />}
                          onClick={(e) => handleRefundClick(e, membership)}
                          style={{ flex: 1, background: 'var(--hover-bg)', borderColor: '#ff4d4f', color: '#ff4d4f' }}
                        >
                          Refund
                        </Button>
                      </div>
                    )}
                  </div>
                  {!isLocker && (() => {
                    const activeTab = bottomTab[membership._id] || 'freeze';
                    return (
                      <div className="card-tabs" onClick={(e) => e.stopPropagation()}>
                        <div className="card-tabs-head">
                          <button
                            className={`card-tab ${activeTab === 'freeze' ? 'active' : ''}`}
                            onClick={() => setTab(membership._id, 'freeze')}
                          >
                            {membership.type === 'addon' ? 'Freeze Days' : 'Freeze Slots'}
                          </button>
                          <button
                            className={`card-tab ${activeTab === 'extend' ? 'active' : ''}`}
                            onClick={() => setTab(membership._id, 'extend')}
                          >
                            Extend Days
                          </button>
                        </div>

                        <div className="card-tabs-body">
                          {activeTab === 'freeze' ? (
                            (membership.freezeHistory?.length ?? 0) > 0 ? (
                              membership.freezeHistory!.map((f, i) => (
                                <div className="freeze-item" key={i}>
                                  <span className="freeze-idx">{i + 1}</span>
                                  <span className="freeze-range">
                                    {fmtFreezeDate(f.freezeStartDate)} - {fmtFreezeDate(f.freezeEndDate)}
                                  </span>
                                  {(() => {
                                    const days = f.freezeDays ?? (membership.freezeHistory!.length === 1 ? (membership.totalFrozenDays ?? membership.freezeDays) : null);
                                    return days != null ? (
                                      <span className="freeze-days">{days} {days === 1 ? 'day' : 'days'}</span>
                                    ) : null;
                                  })()}
                                </div>
                              ))
                            ) : (
                              <div className="freeze-empty">No freeze slots used</div>
                            )
                          ) : (
                            (membership.extendHistory?.length ?? 0) > 0 ? (
                              membership.extendHistory!.map((e, i) => (
                                <div className="freeze-item" key={i}>
                                  <span className="freeze-idx">{i + 1}</span>
                                  <span className="freeze-range">
                                    {fmtFreezeDate(e.previousExpiryDate)} → {fmtFreezeDate(e.newExpiryDate)}
                                  </span>
                                  {e.daysExtended != null && (
                                    <span className="freeze-days">+{e.daysExtended} {e.daysExtended === 1 ? 'day' : 'days'}</span>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="freeze-empty">No days extended</div>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <FreezeAddonModal
        visible={freezeModalVisible}
        onCancel={handleFreezeModalClose}
        selectedMembership={freezeMembership}
      />

      <RefundModal
        open={!!refundMembership}
        membershipId={refundMembership?._id}
        isAddon={refundMembership?.type === 'addon'}
        invoice={refundMembership ? {
          _id:             refundMembership.invoices?.[0]?._id,
          invoiceNumber:   refundMembership.invoices?.[0]?.invoiceNumber,
          packageName:     refundMembership.planId?.name,
          // A membership can have multiple invoices (new client, upgrade,
          // balance-clear, …) — the refundable amount is their combined total.
          // `totalInvoiced` from the API isn't reliable for this (it can
          // reflect just one invoice), so sum the invoices directly.
          totalAmount:     refundMembership.invoices?.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0) ?? 0,
          startDate:       refundMembership.startDate,
          expiryDate:      getEffectiveExpiry(
            refundMembership,
            refundMembership.type === 'locker' || refundMembership.addonType === 'locker',
          ),
          customerName:    customerName,
          customerNumber:  customerMobile,
        } : null}
        onClose={handleRefundModalClose}
        onSuccess={onRefundSuccess}
      />

      {/* Change Trainer Modal */}
      <Modal
        open={trainerModalVisible}
        onCancel={handleTrainerModalClose}
        title="Change Trainer"
        onOk={handleTrainerSubmit}
        okText="Save"
        confirmLoading={changingTrainer}
        okButtonProps={{ disabled: !selectedTrainerId }}
        width={400}
        destroyOnClose
      >
        <div style={{ padding: '12px 0' }}>
          <p style={{ marginBottom: 8 }}>Select Trainer</p>
          {(() => {
            const allTrainers: any[] = (trainersData as any)?.trainers || (trainersData as any)?.data || [];
            const addonType = trainerMembership?.addonType;
            const normalizeStr = (s: string) => s.toLowerCase().replace(/_/g, ' ').trim();
            const filtered = addonType
              ? allTrainers.filter(t => {
                  const specs: string[] = t.specialization || [];
                  return specs.some(s => normalizeStr(s) === normalizeStr(addonType));
                })
              : allTrainers;
            return (
              <Select
                style={{ width: '100%' }}
                placeholder="Select a trainer"
                value={selectedTrainerId}
                onChange={(val: string) => setSelectedTrainerId(val)}
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={filtered.map(t => ({
                  value: t._id,
                  label: t.name || t.user?.name || t._id,
                }))}
                notFoundContent={
                  addonType && filtered.length === 0
                    ? `No trainers found for ${normalizeStr(addonType).replace(/\b\w/g, c => c.toUpperCase())}`
                    : undefined
                }
              />
            );
          })()}
        </div>
      </Modal>

      {/* Change Trainer Confirmation Modal */}
      <Modal
        open={trainerConfirmVisible}
        onCancel={() => setTrainerConfirmVisible(false)}
        title="Confirm Change"
        onOk={handleTrainerConfirm}
        okText="Yes, Change"
        cancelText="Cancel"
        confirmLoading={changingTrainer}
        centered
        width={360}
        okButtonProps={{ style: { background: '#22c55e', borderColor: '#22c55e' } }}
      >
        <p style={{ margin: '12px 0' }}>Are you sure you want to change the trainer ?</p>
      </Modal>

      {/* Add Days (extend expiry) Modal */}
      <Modal
        open={!!addDaysMembership}
        onCancel={handleAddDaysClose}
        title="Add Days"
        onOk={handleAddDaysSubmit}
        okText="Save"
        confirmLoading={extendingDays}
        okButtonProps={{ disabled: !extraDays || Number(extraDays) <= 0 }}
        width={560}
        className="add-days-modal"
        destroyOnClose
      >
        {(() => {
          const isAddDaysLocker = addDaysMembership?.type === 'locker' || addDaysMembership?.addonType === 'locker';
          // Original Expiry Date is the raw, never-extended expiry; the extension (and any
          // further extra days typed below) builds on top of the current EFFECTIVE expiry.
          const originalExpiry  = addDaysMembership?.expiryDate ? dayjs(addDaysMembership.expiryDate) : null;
          const effectiveExpiry = addDaysMembership ? getEffectiveExpiry(addDaysMembership, isAddDaysLocker) : undefined;
          const currentExpiry   = effectiveExpiry ? dayjs(effectiveExpiry) : originalExpiry;
          const newExpiry = currentExpiry && extraDays && Number(extraDays) > 0
            ? currentExpiry.add(Number(extraDays), 'day')
            : currentExpiry;
          return (
            <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, color: 'var(--sider-text)' }}>Extra Days</label>
                <Input
                  type="number"
                  min={1}
                  placeholder="Enter extra days"
                  value={extraDays}
                  onChange={(e) => setExtraDays(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ fontSize: 13, color: 'var(--sider-text)' }}>Original Expiry Date</label>
                  <DatePicker style={{ width: '100%' }} value={originalExpiry} format="DD-MM-YYYY" disabled />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ fontSize: 13, color: 'var(--sider-text)' }}>New Expiry Date</label>
                  <DatePicker style={{ width: '100%' }} value={newExpiry} format="DD-MM-YYYY" disabled />
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Complementary Services Modal */}
      <Modal
        open={couponModalVisible}
        onCancel={handleCouponModalClose}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <GiftOutlined style={{ color: 'var(--accent)' }} />
            <span>Complementary Coupons</span>
            {couponMembership?.planId?.numberOfCoupons && (
              <span style={{ fontSize: 12, color: '#999', fontWeight: 400 }}>
                ({couponMembership.planId.numberOfCoupons} coupons available)
              </span>
            )}
          </div>
        }
        footer={null}
        width={520}
        destroyOnClose
      >
        {alreadyAddedServices.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>Already Added:</p>
            {alreadyAddedServices.map((svc, i) => {
              const isEditing = editingServiceType === svc.serviceType;
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', background: 'var(--card-bg)', borderRadius: 6, marginBottom: 4, fontSize: 13, gap: 8 }}>
                  <div>
                    <div>{svc.serviceType}</div>
                    {svc.createdBy?.name && (
                      <div style={{ fontSize: 11, color: '#999' }}>by {svc.createdBy.name}</div>
                    )}
                  </div>
                  {isEditing ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <InputNumber
                        size="small"
                        min={0}
                        value={editSessionsValue ?? undefined}
                        onChange={(v) => setEditSessionsValue(v as number)}
                        style={{ width: 70 }}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<CheckOutlined />}
                        loading={savingServiceEdit}
                        onClick={() => handleEditServiceSave(svc)}
                      />
                      <Button type="text" size="small" icon={<CloseOutlined />} onClick={handleEditServiceCancel} />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#999' }}>{getComplementarySessionCount(svc)} session{getComplementarySessionCount(svc) !== 1 ? 's' : ''}</span>
                      {hasPermission('COUPONS_EDIT') && (
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditServiceStart(svc)} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <Form form={couponForm} onFinish={handleCouponSubmit} onFinishFailed={onFinishFailed} layout="vertical">
          <div style={{ display: 'flex', gap: 8, marginBottom: 6, paddingRight: 36 }}>
            <div style={{ flex: 2, fontSize: 13, fontWeight: 600 }}><span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>Service Type</div>
            <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}><span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>Sessions</div>
          </div>

          <Form.List name="services" initialValue={[{}]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ display: 'flex', gap: 8, alignItems: 'self-start', marginBottom: 8 }}>
                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue }) => {
                        const allServices: any[] = getFieldValue('services') || [];
                        const selectedElsewhere = allServices.filter((_, i) => i !== name).map((s: any) => s?.serviceType).filter(Boolean);
                        const options = SERVICE_OPTIONS.map(opt => ({
                          ...opt,
                          disabled: selectedElsewhere.includes(opt.value),
                        }));
                        return (
                          <Form.Item {...restField} name={[name, 'serviceType']} rules={[{ required: true, message: 'Select service' }]} style={{ flex: 2, marginBottom: 0 }}>
                            <Select placeholder="Select service" options={options} />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>

                    <Form.Item {...restField} name={[name, 'numberOfSessions']} rules={[{ required: true, message: 'Required' }, { type: 'number', min: 1, message: 'Must be at least 1' }]} style={{ flex: 1, marginBottom: 0 }}>
                      <InputNumber placeholder="Enter sessions" min={1} style={{ width: '100%', height: '32px' }} />
                    </Form.Item>

                    <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} disabled={fields.length === 1} style={{ flexShrink: 0, width: 28, padding: 0 }} />
                  </div>
                ))}

                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => {
                    const allServices: any[] = getFieldValue('services') || [];
                    const selectedTypes = allServices.map((s: any) => s?.serviceType).filter(Boolean);
                    const hasMore = SERVICE_OPTIONS.some(opt => !selectedTypes.includes(opt.value));
                    return (
                      <Form.Item style={{ marginBottom: 8 }}>
                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} disabled={!hasMore}>
                          Add Another Service
                        </Button>
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldsError, getFieldValue }) => {
              const totalCoupons   = couponMembership?.planId?.numberOfCoupons || 0;
              const services: any[] = getFieldValue('services') || [];
              const formTypes      = services.map((s: any) => s?.serviceType).filter(Boolean);
              // A service type re-selected in the form overwrites its existing total
              // (the backend sets, not adds), so exclude it from the existing count.
              const existingTotal  = alreadyAddedServices
                .filter((s: any) => !formTypes.includes(s?.serviceType))
                .reduce((sum, s) => sum + getComplementarySessionCount(s), 0);
              const formTotal      = services.reduce((sum: number, s: any) => sum + (s?.numberOfSessions || 0), 0);
              const allocated      = existingTotal + formTotal;
              const remaining      = totalCoupons - allocated;
              const hasErrors      = getFieldsError().some(f => f.errors.length > 0);
              const allFilled      = services.length > 0 && services.every((s: any) => s?.serviceType && s?.numberOfSessions);
              // No new service picked at all — nothing to submit, just let Save close the modal.
              const allEmpty       = services.every((s: any) => !s?.serviceType && !s?.numberOfSessions);
              const isExact        = allocated === totalCoupons;

              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, marginBottom: 12, background: isExact ? 'rgba(82,196,26,0.08)' : remaining < 0 ? 'rgba(255,77,79,0.08)' : 'rgba(250,173,20,0.08)', border: `1px solid ${isExact ? '#52c41a' : remaining < 0 ? '#ff4d4f' : '#faad14'}` }}>
                    <span style={{ fontSize: 13 }}>Coupons allocated: <strong>{allocated}</strong> / <strong>{totalCoupons}</strong></span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: isExact ? '#52c41a' : remaining < 0 ? '#ff4d4f' : '#faad14' }}>
                      {isExact ? '✓ All coupons used' : remaining > 0 ? `${remaining} remaining` : `${Math.abs(remaining)} over limit`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button onClick={handleCouponModalClose}>Cancel</Button>
                    <Button
                      type="primary"
                      loading={addingService}
                      disabled={!allEmpty && (hasErrors || !allFilled || remaining < 0 || !hasPermission('COUPONS_CREATE'))}
                      onClick={() => { if (allEmpty) { handleCouponModalClose(); } else { couponForm.submit(); } }}
                    >
                      Save
                    </Button>
                  </div>
                </>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default MembershipCard;
